import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_ROOT } from './lib/api';
import { translations as baseTranslations } from './i18n';
import { getContent } from './content';

const LangContext = createContext(null);

const LS_KEY = 'horren_lang_v1';
const isPlainObject = (value) => value != null && typeof value === 'object' && !Array.isArray(value);

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      // Sanctioned UI-preference (nl|de). Not sensitive data. See .eslintrc.json.
      // eslint-disable-next-line no-restricted-properties
      const stored = localStorage.getItem(LS_KEY);
      if (stored === 'nl' || stored === 'de') return stored;
    } catch (e) {
      // localStorage may be blocked (Safari private mode, embedded webview).
      // Falling back to default lang is the intended behaviour.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[LangContext] localStorage read blocked:', e?.message);
      }
    }
    return 'nl';
  });

  useEffect(() => {
    document.documentElement.lang = lang;
    try {
      // Sanctioned UI-preference (nl|de). Not sensitive data. See .eslintrc.json.
      // eslint-disable-next-line no-restricted-properties
      localStorage.setItem(LS_KEY, lang);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[LangContext] localStorage write blocked:', e?.message);
      }
    }
  }, [lang]);

  // Content overlay from /api/content: sparse override of i18n strings,
  // company info, media URLs. Refreshed when the tab regains focus so admin
  // changes do not leave stale page data in open tabs.
  const [overlay, setOverlay] = useState(null);
  const [contentReady, setContentReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let lastFetchAt = 0;
    let refreshTimer = null;

    const loadContent = () => {
      lastFetchAt = Date.now();
      return axios.get(`${API_ROOT}/content`, {
        params: { _: lastFetchAt },
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: '0',
        },
      }).then(({ data }) => {
        if (cancelled) return;
        // An empty {} means "no overlay" and clears a previous overlay.
        const hasOverlay =
          isPlainObject(data?.translations) ||
          isPlainObject(data?.company) ||
          isPlainObject(data?.media) ||
          isPlainObject(data?.seo) ||
          isPlainObject(data?.mediaMeta) ||
          Array.isArray(data?.pages) ||
          Array.isArray(data?.navigation);
        setOverlay(data && hasOverlay ? data : null);
        setContentReady(true);
      }).catch((e) => {
        if (!cancelled) setContentReady(true);
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[LangContext] content overlay fetch failed:', e?.message);
        }
      });
    };

    const refreshIfStale = () => {
      if (Date.now() - lastFetchAt < 15000) return;
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(loadContent, 120);
    };
    const onVisibility = () => {
      if (!document.hidden) refreshIfStale();
    };

    loadContent();
    window.addEventListener('focus', refreshIfStale);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      window.clearTimeout(refreshTimer);
      window.removeEventListener('focus', refreshIfStale);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const t = useMemo(() => {
    if (!isPlainObject(overlay?.translations)) return baseTranslations[lang];
    // getContent applies the merge; we re-use its merged translations.
    return getContent(lang, overlay).raw;
  }, [lang, overlay]);
  const c = useMemo(() => getContent(lang, overlay), [lang, overlay]);

  return (
    <LangContext.Provider value={{ lang, setLang, t, c, overlay, contentReady }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used within LangProvider');
  return ctx;
}
